import { FC, useCallback, useState } from 'react';
import LoaderWrapper from '@/components/ui/LoaderWrapper/LoaderWrapper.tsx';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@mui/material';
import Toggle from '@/components/shared/Toggle/Toggle.tsx';
import FaqTable from '@/components/functional/FaqTable/FaqTable.tsx';
import useFaq from '@/api/useFaq.ts';
import AddFaqModal from '@/components/modals/Faq/AddFaqModal.tsx';
import EditFaqModal from '@/components/modals/Faq/EditFaqModal.tsx';
import DeleteFaqModal from '@/components/modals/Faq/DeleteFaqModal.tsx';
import CopyFaqModal from '@/components/modals/Faq/CopyFaqModal.tsx';

const FaqPage: FC = () => {
  const { t } = useTranslation();
  const [selectedElement, setSelectedElement] = useState<number | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { data: faqs, isLoading: isLoadingFaqs, isError: isErrorFaqs, error: errorFaqs } = useFaq();

  const handleSelectedElementChange = useCallback((selectedElements: number[]) => {
    setSelectedElement(selectedElements[0] ?? null);
  }, []);

  const handleRowClick = useCallback((selectedElement: number) => {
    setSelectedElement(selectedElement ?? null);
    setIsEditModalOpen(true);
  }, []);

  const hasSelectedElement = !!selectedElement;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-4">
        <h1 className="text-3xl font-medium m-0 font-ubuntu">{t('pages.faq.title')}</h1>
      </div>
      {isErrorFaqs && <Alert severity="error">{errorFaqs?.message}</Alert>}
      <div className="flex justify-end flex-wrap gap-2">
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="info" variant="contained">
              {t('button.add')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => <AddFaqModal isOpen={isOpen} onClose={handleClose} />}
        />
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="info" variant="contained" disabled={!hasSelectedElement}>
              {t('button.copy')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <CopyFaqModal isOpen={isOpen} onClose={handleClose} questionId={selectedElement} />
          )}
        />
        <Button
          onClick={() => {
            setIsEditModalOpen((prevState) => !prevState);
          }}
          color="info"
          variant="contained"
          disabled={!hasSelectedElement}
        >
          {t('button.edit')}
        </Button>
        {isEditModalOpen && (
          <EditFaqModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            questionId={selectedElement}
          />
        )}
        <Toggle
          renderTrigger={({ toggle }) => (
            <Button onClick={toggle} color="error" variant="contained" disabled={!hasSelectedElement}>
              {t('button.delete')}
            </Button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <DeleteFaqModal isOpen={isOpen} onClose={handleClose} selectedElement={selectedElement} />
          )}
        />
      </div>
      <div>
        <LoaderWrapper isLoading={isLoadingFaqs}>
          <FaqTable
            rows={faqs ?? []}
            selectable={true}
            onSelectedChange={handleSelectedElementChange}
            onRowClick={handleRowClick}
          />
        </LoaderWrapper>
      </div>
    </div>
  );
};

export default FaqPage;
